import fetch from "auth/FetchInterceptor";
import {
  getPaginatedTransactions,
  getTransactionDetail,
} from "../mock/data/transactionMockData";

export const TransactionService = {};

// Set this to true to use mock data, false to use real API
const USE_MOCK_DATA = false;

TransactionService.get = async ({
  page,
  pageSize,
  sort,
  startDate,
  endDate,
  outlet,
  type,
  status,
  invoice,
}) => {
  if (USE_MOCK_DATA) {
    // Simulate network delay
    await new Promise((resolve) =>
      setTimeout(resolve, 200 + Math.random() * 300),
    );

    return getPaginatedTransactions({
      page: parseInt(page) || 1,
      pageSize: parseInt(pageSize) || 10,
      sort,
      startDate,
      endDate,
      outlet,
      type,
    });
  } else {
    // Original API call
    let apiUrl = "/v1/trx?page=" + page + "&per_page=" + pageSize;
    if (startDate) apiUrl += "&date_from=" + startDate;
    if (endDate) apiUrl += "&date_to=" + endDate;
    if (outlet) apiUrl += "&outlet=" + outlet;
    if (type) apiUrl += "&type=" + type;
    if (status) apiUrl += "&status=" + status;
    if (invoice) apiUrl += "&inv=" + encodeURIComponent(invoice);
    if (sort === "ascend") apiUrl += "&order_by=%2Dcreated_at";
    else if (sort === "descend") apiUrl += "&order_by=%2Bcreated_at";
    return fetch({
      url: apiUrl,
      method: "get",
      timeout: pageSize >= 1000 ? 120000 : undefined, // 2 min timeout for large fetches
    });
  }
};

// Fetch all transactions concurrently in batches to avoid timeout
// First request gets total/last_page, then remaining pages fire in parallel
TransactionService.getAll = async ({
  sort,
  startDate,
  endDate,
  outlet,
  type,
  status,
  invoice,
  batchSize = 1000,
  concurrency = 5, // max parallel requests
  onBatchLoaded,
}) => {
  const fetchParams = { sort, startDate, endDate, outlet, type, status, invoice };

  // First request to determine total pages
  const firstResponse = await TransactionService.get({
    page: 1,
    pageSize: batchSize,
    ...fetchParams,
  });

  const firstBatch = firstResponse.data?.data || [];
  const total = firstResponse.data?.total || 0;
  const totalPages = firstResponse.data?.last_page || Math.ceil(total / batchSize);

  // Initialize results array with first batch at index 0
  const results = new Array(totalPages).fill(null);
  results[0] = firstBatch;
  let loadedCount = firstBatch.length;

  if (onBatchLoaded) {
    onBatchLoaded({
      loaded: loadedCount,
      total,
      currentPage: 1,
      totalPages,
      data: firstBatch,
    });
  }

  if (totalPages <= 1) {
    return firstBatch;
  }

  // Fetch remaining pages concurrently with concurrency limit
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

  const fetchPage = async (page) => {
    const response = await TransactionService.get({
      page,
      pageSize: batchSize,
      ...fetchParams,
    });
    return { page, data: response.data?.data || [] };
  };

  // Process in chunks to limit concurrency
  for (let i = 0; i < remainingPages.length; i += concurrency) {
    const chunk = remainingPages.slice(i, i + concurrency);
    const chunkResults = await Promise.all(chunk.map(fetchPage));

    for (const result of chunkResults) {
      results[result.page - 1] = result.data;
      loadedCount += result.data.length;
    }

    // Flatten loaded results so far and notify progress
    const allDataSoFar = results.filter(Boolean).flat();
    if (onBatchLoaded) {
      onBatchLoaded({
        loaded: loadedCount,
        total,
        currentPage: Math.min(i + concurrency + 1, totalPages),
        totalPages,
        data: allDataSoFar,
      });
    }
  }

  return results.flat();
};

TransactionService.getDetail = async (inv) => {
  if (USE_MOCK_DATA) {
    // Simulate network delay
    await new Promise((resolve) =>
      setTimeout(resolve, 100 + Math.random() * 200),
    );

    return getTransactionDetail(inv);
  } else {
    // Original API call
    return fetch({
      url: "/v4/trx/?inv=" + inv,
      method: "get",
    });
  }
};

export default TransactionService;
